// Stroke-averaged boat speed / split for non-Erg modes.
// Speed samples = GPS-reported speed (or full-precision lat/lng deltas when the
// receiver gives none). At each catch: speed = distance over the last N
// complete strokes ÷ their time. Before N strokes exist, or when no catch
// for 1.6·T, the last 6 s average is used instead. < 0.5 m/s → no split.
const FALLBACK_WINDOW_MS = 6000;
const HISTORY_MS = 120000;
export const MIN_SPLIT_SPEED = 0.5;
export const SPLIT_SMOOTHING_OPTIONS = [1, 3, 5, 10];
export const DEFAULT_SPLIT_STROKES = 3;

const R = 6371000;
export function haversineM(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function createStrokeSplitSmoother() {
  let samples = [];   // [{t, v}] — v holds from t until the next sample
  let catches = [];   // catch timestamps
  let lastPos = null;
  let lastExpectedMs = 2000;
  let held = null;    // stroke-based speed, updated at each catch

  const trim = (now) => {
    while (samples.length > 1 && now - samples[1].t > HISTORY_MS) samples.shift();
    while (catches.length && now - catches[0] > HISTORY_MS) catches.shift();
  };

  // Time-weighted distance from sample-and-hold speeds over [t0, t1].
  const distanceBetween = (t0, t1) => {
    let d = 0;
    for (let i = 0; i < samples.length; i++) {
      const s = samples[i].t;
      const e = i + 1 < samples.length ? samples[i + 1].t : t1;
      const lo = Math.max(s, t0);
      const hi = Math.min(e, t1);
      if (hi > lo) d += samples[i].v * (hi - lo) / 1000;
    }
    return d;
  };

  function addSpeed(t, v) {
    if (!Number.isFinite(v) || v < 0) return;
    samples.push({ t, v });
    trim(t);
  }

  // GPS fix: prefer reported speed; otherwise derive from full-precision coords.
  function addFix(t, { lat, lng, speed }) {
    if (typeof speed === 'number' && Number.isFinite(speed) && speed >= 0) {
      addSpeed(t, speed);
    } else if (lastPos && Number.isFinite(lat) && Number.isFinite(lng) && t > lastPos.t) {
      const v = haversineM(lastPos, { lat, lng }) / ((t - lastPos.t) / 1000);
      if (v < 15) addSpeed(t, v);
    }
    if (Number.isFinite(lat) && Number.isFinite(lng)) lastPos = { t, lat, lng };
  }

  function onCatch(t, strokesN, expectedMs) {
    if (expectedMs > 0) lastExpectedMs = expectedMs;
    catches.push(t);
    trim(t);
    if (catches.length > strokesN) {
      const t0 = catches[catches.length - 1 - strokesN];
      const dur = (t - t0) / 1000;
      held = dur > 0 ? { v: distanceBetween(t0, t) / dur, t } : null;
    } else {
      held = null;
    }
    return held ? held.v : null;
  }

  // Mean of the last k per-stroke splits (s/500 m); null until k strokes exist.
  function avgStrokeSplit(k) {
    if (catches.length <= k) return null;
    let sum = 0;
    for (let i = catches.length - k; i < catches.length; i += 1) {
      const dur = (catches[i] - catches[i - 1]) / 1000;
      const v = dur > 0 ? distanceBetween(catches[i - 1], catches[i]) / dur : 0;
      if (v < MIN_SPLIT_SPEED) return null;
      sum += 500 / v;
    }
    return sum / k;
  }

  function fallbackSpeed(now) {
    const t0 = now - FALLBACK_WINDOW_MS;
    if (!samples.length) return 0;
    const start = Math.max(t0, samples[0].t);
    const dur = (now - start) / 1000;
    return dur > 0 ? distanceBetween(start, now) / dur : samples[samples.length - 1].v;
  }

  // Speed to display now.
  function current(now) {
    const lastCatch = catches[catches.length - 1] || 0;
    const fresh = held && now - lastCatch <= 1.6 * lastExpectedMs;
    return fresh ? { speed: held.v, mode: 'stroke' } : { speed: fallbackSpeed(now), mode: 'time' };
  }

  return {
    addSpeed, addFix, onCatch, current, avgStrokeSplit,
    reset() { samples = []; catches = []; lastPos = null; held = null; lastExpectedMs = 2000; },
  };
}

export const speedToSplit = (v) => (v >= MIN_SPLIT_SPEED ? 500 / v : 0);

// M:SS.s ; 0 / invalid → --:--
export function formatSplitTenths(seconds) {
  if (!(seconds > 0) || seconds >= 600) return '--:--';
  const tenths = Math.round(seconds * 10);
  const m = Math.floor(tenths / 600);
  const s = (tenths % 600) / 10;
  return `${m}:${s < 10 ? '0' : ''}${s.toFixed(1)}`;
}

export function getSplitSmoothingStrokes() {
  try {
    const n = parseInt(localStorage.getItem('split_smoothing_strokes'), 10);
    return SPLIT_SMOOTHING_OPTIONS.includes(n) ? n : DEFAULT_SPLIT_STROKES;
  } catch { return DEFAULT_SPLIT_STROKES; }
}
